import React, { useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO from "./SEO";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: `What is ${import.meta.env.VITE_COMPANY_NAME}?`,
    answer: `${import.meta.env.VITE_COMPANY_NAME} is a promising Fintech company and registered NBFC (${import.meta.env.VITE_COMPANY_NBFC}). With a team of experienced professionals in the financial industry, we offer a wide range of financial services including loans and more. Our goal is to provide affordable and accessible financial services to everyone.`,
  },
  {
    question: "Who is eligible for a personal loan?",
    answer: `To be eligible for a personal loan, you must be:\n• A salaried individual\n• Aged between 21 and 60 years\n• Employed with a recognized organization for at least 6 months\n• Earning a minimum monthly salary as per our criteria.`,
  },
  {
    question: "What documents are required?",
    answer: `You will need to submit the following documents:\n• Proof of identity (Aadhaar, PAN card, passport, etc.)\n• Proof of address (utility bills, Aadhaar, etc.)\n• Salary slips for the last three months\n• Bank statements for the last six months.`,
  },
  {
    question: "How much can I borrow?",
    answer: `${import.meta.env.VITE_COMPANY_NAME} offers personal loans ranging from ₹5,000 to ₹1,00,000 depending on your salary, repayment capacity, and credit score.`,
  },
  {
    question: "What is the interest rate?",
    answer: `The interest rate varies based on your credit profile, loan amount, and tenure. Typically, our rates are competitive and designed to be affordable for salaried individuals. Contact us to get a personalized quote.`,
  },
  {
    question: "How long does it take to get a loan?",
    answer: `Once you submit your application and documents, our team processes your request quickly. Loan approval usually takes a few minutes, and funds are disbursed within 5 minutes upon approval.`,
  },
  {
    question: "Can I repay my loan early?",
    answer: `Yes, ${import.meta.env.VITE_COMPANY_NAME} allows early repayment of loans. Please review your loan agreement or contact us for details.`,
  },
  {
    question: "How is my eligibility assessed?",
    answer: `We assess your loan eligibility based on factors such as your monthly income, credit score, job stability, and repayment history. A good credit score increases your chances of getting approved with a favorable interest rate.`,
  },
  {
    question: "Can I apply with a low credit score?",
    answer: `Yes, you can still apply for a loan with a low credit score. However, the approval decision and interest rate may vary based on your overall financial profile and ability to repay the loan.`,
  },
  {
    question: "What are the repayment options?",
    answer: `You can repay your loan through various payment options such as NACH (National Automated Clearing House), direct debit, or online payments via our website.`,
  },
  {
    question: "Is my data secure?",
    answer: `Absolutely. ${import.meta.env.VITE_COMPANY_NAME} uses advanced security measures, including encryption and secure servers, to ensure your personal and financial data is protected. We adhere to all data privacy regulations and take confidentiality seriously.`,
  },
  {
    question: "Can I track my loan application?",
    answer: `Yes, you can easily track the status of your loan application through our website by logging into your account. You will also receive updates via email and SMS at each stage of the process.`,
  },
  {
    question: "How can I contact customer support?",
    answer: `You can reach our customer support team through our website's contact form, by email at ${import.meta.env.VITE_COMPANY_EMAIL}, or by calling our helpline during business hours. We're here to assist you with any questions or concerns.`,
  },
];

const FAQ: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleIndex = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white text-text-primary font-sans selection:bg-blue-100 selection:text-primary">
      <SEO
        title="FAQ"
        description={`Frequently asked questions about ${import.meta.env.VITE_COMPANY_NAME}. Learn about loan eligibility, documents, interest rates, and more.`}
        keywords={`loan FAQ, personal loan help India, ${import.meta.env.VITE_COMPANY_NAME} support`}
      />

      <div className="fixed inset-0 pointer-events-none z-0 bg-gradient-to-b from-white via-blue-50/40 to-white" />
      <div className="relative z-20"><Navbar /></div>

      {/* Hero */}
      <section className="w-full pt-40 pb-16 text-center px-6 relative">
        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-white border border-blue-100 shadow-sm rounded-full mb-8">
            <div className="w-2.5 h-2.5 rounded-full bg-primary" />
            <span className="text-xs font-bold tracking-widest text-text-secondary uppercase">Help Center</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-6 text-text-primary">
            Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Questions.</span>
          </h1>
          <p className="text-text-secondary text-lg md:text-xl max-w-2xl mx-auto font-medium leading-relaxed">
            Everything you need to know about loans, eligibility, and our services at {import.meta.env.VITE_COMPANY_NAME}.
          </p>
        </div>
      </section>

      {/* FAQ List */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 pb-24">
        <div className="flex flex-col gap-3">
          {faqData.map((item, idx) => {
            const isOpen = activeIndex === idx;
            return (
              <div
                key={idx}
                className={`rounded-2xl border transition-colors ${isOpen ? 'border-primary/30 bg-blue-50/40' : 'border-gray-100 bg-white hover:border-primary/20'}`}
              >
                <button
                  onClick={() => toggleIndex(idx)}
                  className="w-full flex justify-between items-center px-7 py-6 text-left gap-4"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-extrabold text-primary bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1 shrink-0">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <span className={`font-semibold text-base md:text-lg leading-snug ${isOpen ? 'text-primary' : 'text-text-primary'}`}>
                      {item.question}
                    </span>
                  </div>
                  <div className={`shrink-0 w-9 h-9 rounded-xl border flex items-center justify-center transition-colors ${isOpen ? 'bg-primary border-primary text-white' : 'border-gray-200 text-text-secondary'}`}>
                    {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-7 pb-7 pt-1">
                    <div className="pl-10 border-t border-blue-100 pt-5">
                      <p className="text-text-secondary text-base font-medium whitespace-pre-line leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 p-8 rounded-3xl bg-blue-50/60 border border-blue-100 text-center">
          <p className="text-text-secondary font-medium mb-1">Still have questions?</p>
          <p className="text-text-primary font-bold mb-5">Our support team is here to help.</p>
          <a
            href="/contact"
            className="inline-block px-8 py-3 bg-primary text-white font-bold rounded-2xl hover:bg-secondary transition-all shadow-md text-sm"
          >
            Contact Us
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQ;
